import * as countries from "i18n-iso-countries";
import english from "i18n-iso-countries/langs/en.json";
import addressRegions from "../../../backend/resources/address-country-regions.json";

countries.registerLocale(english);

export interface CompanyAddressDetails {
    address?: string | null;
    street1?: string | null;
    street2?: string | null;
    city?: string | null;
    state?: string | null;
    zipCode?: string | null;
    country?: string | null;
}

export interface CountryOption {
    code: string | null;
    label: string;
}

export const unknownCountry: CountryOption = {code: null, label: "Unknown"};
const normalizeRegion = (value: string) => value.normalize("NFD").replace(/[\u0300-\u036f\s.]+/g, "").toUpperCase();
const regionCountries = new Map(Object.entries(addressRegions).flatMap(([country, regions]) =>
    Object.entries(regions).flatMap(([code, name]) => [[normalizeRegion(code), country], [normalizeRegion(name), country]] as [string, string][])
));

export function getAddressCountry(address: CompanyAddressDetails): string | null {
    return address.country?.trim() || regionCountries.get(normalizeRegion(address.state ?? "")) || null;
}
export const countryOptions: CountryOption[] = [
    unknownCountry,
    {code: "US", label: "United States"},
    ...Object.entries(countries.getNames("en", {select: "official"}))
        .filter(([code]) => code !== "US")
        .map(([code, label]) => ({code, label}))
        .sort((left, right) => left.label.localeCompare(right.label)),
];

export function getCountryCode(value?: string | null): string | null {
    const name = value?.trim();
    if (!name) return null;
    const code = name.toUpperCase();
    if (countries.getAlpha2Codes()[code]) return code;
    return countries.alpha3ToAlpha2(code) || countries.getAlpha2Code(name, "en") || null;
}

export function getCountryOption(value?: string | null): CountryOption {
    const code = getCountryCode(value);
    return countryOptions.find(option => option.code === code) || unknownCountry;
}

/** Matches the server's P.O. box rule (ApplicationInputValidator::PO_BOX). */
export const poBoxPattern = /\b(?:p\s*\.?\s*o\s*\.?|post\s+office)\s*box\b/i;
export const poBoxError = "A physical street address is required; P.O. boxes are not allowed here.";

export function hasPoBox(...streets: (string | null | undefined)[]): boolean {
    return poBoxPattern.test(streets.map(street => street ?? "").join(" "));
}

/** Inclusive ZIP3 ranges and their US state or territory, as in the server's ZipCodeState. */
const zipStateRanges: [number, number, string][] = [
    [5, 5, "NY"], [6, 7, "PR"], [8, 8, "VI"], [9, 9, "PR"], [10, 27, "MA"], [28, 29, "RI"], [30, 38, "NH"],
    [39, 49, "ME"], [50, 59, "VT"], [60, 69, "CT"], [70, 89, "NJ"], [100, 149, "NY"], [150, 196, "PA"],
    [197, 199, "DE"], [200, 200, "DC"], [201, 201, "VA"], [202, 205, "DC"], [206, 219, "MD"], [220, 246, "VA"],
    [247, 268, "WV"], [270, 289, "NC"], [290, 299, "SC"], [300, 319, "GA"], [320, 339, "FL"], [341, 349, "FL"],
    [350, 369, "AL"], [370, 385, "TN"], [386, 397, "MS"], [398, 399, "GA"], [400, 427, "KY"], [430, 459, "OH"],
    [460, 479, "IN"], [480, 499, "MI"], [500, 528, "IA"], [530, 549, "WI"], [550, 567, "MN"], [569, 569, "DC"],
    [570, 577, "SD"], [580, 588, "ND"], [590, 599, "MT"], [600, 629, "IL"], [630, 658, "MO"], [660, 679, "KS"],
    [680, 693, "NE"], [700, 714, "LA"], [716, 729, "AR"], [730, 732, "OK"], [733, 733, "TX"], [734, 749, "OK"],
    [750, 799, "TX"], [800, 816, "CO"], [820, 831, "WY"], [832, 838, "ID"], [840, 847, "UT"], [850, 865, "AZ"],
    [870, 884, "NM"], [885, 885, "TX"], [889, 898, "NV"], [900, 961, "CA"], [967, 968, "HI"], [969, 969, "GU"],
    [970, 979, "OR"], [980, 994, "WA"], [995, 999, "AK"],
];

export function zipCodeState(zip?: string | null): string | null {
    const match = /^(\d{3})\d{2}(-?\d{4})?$/.exec((zip ?? "").trim());
    if (!match) return null;
    const prefix = Number(match[1]);
    return zipStateRanges.find(([from, to]) => prefix >= from && prefix <= to)?.[2] ?? null;
}

const usCountryNames = ["US", "USA", "UNITED STATES"];
const stripTags = (value: string) => value.replace(/<[^>]*>/g, "").trim();

/** The server's ApplicationInputValidator::country: leftovers from the website lookup become a real country code. */
function normalizeCountry(value: string, state: string): string {
    const country = stripTags(value).toUpperCase();
    if (country === "UNKNOWN") return country;
    const fromState = getAddressCountry({state});
    if ([...usCountryNames, "UNITED STATES OF AMERICA"].includes(country)) return "US";
    // The website's cut-short "UN" is the United States only when the state is a US one.
    if (country === "UN") return fromState ?? "";
    if (fromState === "US" && country === state) return "US";
    // A country name ("UNITED KINGDOM") becomes its code; the server keeps an unknown name for the visitor to correct.
    if (country !== "" && !/^[A-Z]{2}$/.test(country)) return getCountryCode(country) ?? fromState ?? country;
    return country;
}

/**
 * The same repairs the server makes before validating (ApplicationInputValidator::normalizeAddress): "33186--1530"
 * and "33101 1234" become ZIP+4, a US ZIP+4 cut short ("33186-153") keeps its five digits, a New England ZIP that
 * lost its leading zero gets it back, and a blank state of a US address is filled from the ZIP. Markup and fragments
 * the website lookup left in the country ("UN<br>", "FL<br>") become a country code, and a ZIP it left in the city
 * ("FORT LAUDERDALE 33317 UN<br>") or the country is moved back to the ZIP.
 */
export function normalizeAddressDetails<T extends CompanyAddressDetails>(details: T): T {
    const address: T = {...details};
    let blankZip = (address.zipCode ?? "").trim() === "";
    let match: RegExpExecArray | null;
    if (typeof address.city === "string") {
        address.city = stripTags(address.city);
        const usLike = ["", "UN", "US", "USA", "UNITED STATES"].includes(stripTags(address.country ?? "").toUpperCase());
        if (blankZip && usLike && (match = /^(.*\S)\s+(\d{5}(?:-?\d{4})?)(?:\s+(?:UN|US|USA))?$/i.exec(address.city))) {
            [address.city, address.zipCode, address.country, blankZip] = [match[1], match[2], "US", false];
        }
    }
    if (blankZip && typeof address.country === "string" && /^\d{5}(?:-?\d{4})?$/.test(address.country.trim())) {
        [address.zipCode, address.country] = [address.country.trim(), getAddressCountry({state: stripTags(address.state ?? "")}) ?? ""];
    }
    if (typeof address.state === "string") address.state = stripTags(address.state);
    const state = (address.state ?? "").toUpperCase();
    if (typeof address.country === "string") address.country = normalizeCountry(address.country, state);
    const country = (address.country ?? "").toUpperCase();
    if (typeof address.zipCode !== "string") return address;
    let zip = address.zipCode.trim().replace(/\s*-[\s-]*/g, "-").trim();
    const us = country === "US" || (country === "" && getAddressCountry({state}) === "US");
    if ((match = /^(\d{5})[\s-]?(\d{4})$/.exec(zip))) {
        zip = `${match[1]}-${match[2]}`;
    } else if (us && (match = /^(\d{5})-\d{0,3}$/.exec(zip))) {
        zip = match[1];
    } else if (us && /^\d{4}$/.test(zip) && state !== "" && zipCodeState(`0${zip}`) === state) {
        zip = `0${zip}`;
    }
    const filledState = state === "" && country === "US" ? zipCodeState(zip) : null;
    return {...address, zipCode: zip, ...(filledState ? {state: filledState} : {})};
}

export function getAddressValidationError(details: CompanyAddressDetails): string | null {
    const address = normalizeAddressDetails(details);
    if (!(address.street1 ?? address.address)?.trim()) return "You need to enter an address.";
    if (!address.city?.trim()) return "You need to enter a city.";
    if (getCountryCode(getAddressCountry(address)) === "US") {
        if (!address.state?.trim()) return "You need to enter a state for a US address.";
        if (!address.zipCode?.trim()) return "You need to enter a ZIP code for a US address.";
        if (!/^\d{5}(?:-?\d{4})?$/.test(address.zipCode.trim())) return "Enter a five-digit ZIP code or ZIP+4 for a US address.";
    }
    return null;
}

export function formatAddressLocality(address?: CompanyAddressDetails | null): string {
    return [address?.city?.trim(), [address?.state?.trim(), address?.zipCode?.trim()].filter(Boolean).join(" ")]
        .filter(Boolean).join(", ");
}
